import {
  Component, OnInit, Inject, inject,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';

import { UsersService } from '../../../service/entity-services/users.service';
import { ReportSchedule } from '../../models/saved-report.model';
import { environment } from '../../../../environments/environment';

export interface ReportScheduleDialogData {
  schedule: ReportSchedule | null;
  reportName: string;
}

@Component({
  selector: 'app-report-schedule-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatChipsModule
  ],
  template: `
    <div class="schedule-dialog">
      <h2 class="dialog-title">
        <mat-icon>schedule</mat-icon>
        Schedule Report
      </h2>
      <p class="dialog-subtitle">{{ data.reportName }}</p>

      <mat-divider></mat-divider>

      <div class="dialog-body">
        <!-- Enable/Disable Toggle -->
        <div class="toggle-row">
          <mat-slide-toggle [(ngModel)]="enabled" color="primary">
            {{ enabled ? 'Schedule is active' : 'Schedule is off' }}
          </mat-slide-toggle>
        </div>

        @if (enabled) {
          <!-- Frequency -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Frequency</mat-label>
            <mat-select [(ngModel)]="frequency">
              <mat-option value="daily">Daily</mat-option>
              <mat-option value="weekly">Weekly</mat-option>
              <mat-option value="monthly">Monthly</mat-option>
            </mat-select>
          </mat-form-field>

          <!-- Day of Week (for weekly) -->
          @if (frequency === 'weekly') {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Day of Week</mat-label>
              <mat-select [(ngModel)]="dayOfWeek">
                @for (day of daysOfWeek; track day.value) {
                  <mat-option [value]="day.value">{{ day.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }

          <!-- Day of Month (for monthly) -->
          @if (frequency === 'monthly') {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Day of Month</mat-label>
              <mat-select [(ngModel)]="dayOfMonth">
                @for (d of daysOfMonth; track d) {
                  <mat-option [value]="d">{{ d }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }

          <!-- Time -->
          <div class="time-row">
            <mat-form-field appearance="outline" class="time-field">
              <mat-label>Hour</mat-label>
              <mat-select [(ngModel)]="hour">
                @for (h of hours; track h.value) {
                  <mat-option [value]="h.value">{{ h.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="time-field">
              <mat-label>Minute</mat-label>
              <mat-select [(ngModel)]="minute">
                @for (m of minutes; track m) {
                  <mat-option [value]="m">{{ m.toString().padStart(2, '0') }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="time-field">
              <mat-label>AM/PM</mat-label>
              <mat-select [(ngModel)]="ampm">
                <mat-option value="AM">AM</mat-option>
                <mat-option value="PM">PM</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <!-- Schedule Summary -->
          <div class="schedule-summary">
            <mat-icon>info</mat-icon>
            <span>{{ getScheduleSummary() }}</span>
          </div>

          <mat-divider></mat-divider>

          <!-- Recipients -->
          <h4 class="section-heading">Email Recipients</h4>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Send to users</mat-label>
            <mat-select [(ngModel)]="selectedUsers" multiple>
              @for (user of allUsers; track user.email) {
                <mat-option [value]="user.email">{{ user.friendlyName || user.email }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>External email addresses</mat-label>
            <input matInput
                   [(ngModel)]="externalEmails"
                   placeholder="comma-separated emails" />
            <mat-hint>For recipients outside your organization</mat-hint>
          </mat-form-field>

          @if (getAllRecipients().length === 0) {
            <p class="recipient-warning">
              Add at least one recipient for scheduled emails.
            </p>
          }
        }
      </div>

      <mat-divider></mat-divider>

      <div class="dialog-actions">
        <button mat-button (click)="close()">Cancel</button>
        <button mat-raised-button color="primary" (click)="save()">
          <mat-icon>check</mat-icon>
          {{ enabled ? 'Save Schedule' : 'Turn Off Schedule' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .schedule-dialog {
      width: 480px;
      max-width: 90vw;
    }

    .dialog-title {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0;
      padding: 20px 24px 4px;
      font-size: 20px;
      font-weight: 500;
    }

    .dialog-title mat-icon {
      color: #1976d2;
    }

    .dialog-subtitle {
      margin: 0;
      padding: 0 24px 16px;
      font-size: 14px;
      color: #666;
    }

    .dialog-body {
      padding: 20px 24px;
    }

    .toggle-row {
      margin-bottom: 20px;
    }

    .full-width {
      width: 100%;
      margin-bottom: 4px;
    }

    .time-row {
      display: flex;
      gap: 12px;
    }

    .time-field {
      flex: 1;
    }

    .schedule-summary {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 12px 16px;
      background: #e3f2fd;
      border-radius: 8px;
      margin: 8px 0 16px;
      font-size: 14px;
      color: #1565c0;
    }

    .schedule-summary mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .section-heading {
      margin: 16px 0 8px;
      font-size: 13px;
      font-weight: 600;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .recipient-warning {
      color: #e65100;
      font-size: 12px;
      margin: 4px 0;
    }

    .dialog-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      padding: 12px 24px;
    }
  `]
})
export class ReportScheduleDialogComponent implements OnInit {

  private usersService = inject(UsersService);
  private cdr = inject(ChangeDetectorRef);

  allUsers: any[] = [];

  // Schedule config
  enabled = false;
  frequency: 'daily' | 'weekly' | 'monthly' = 'weekly';
  dayOfWeek = 2; // Tuesday
  dayOfMonth = 1;
  hour = 8;
  minute = 0;
  ampm: 'AM' | 'PM' = 'AM';

  // Recipients
  selectedUsers: string[] = [];
  externalEmails = '';

  // Dropdown data
  daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];
  daysOfMonth = Array.from({ length: 28 }, (_, i) => i + 1);
  hours = Array.from({ length: 12 }, (_, i) => ({
    value: i === 0 ? 12 : i,
    label: (i === 0 ? 12 : i).toString()
  }));
  minutes = [0, 15, 30, 45];

  constructor(
    public dialogRef: MatDialogRef<ReportScheduleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportScheduleDialogData
  ) {}

  ngOnInit(): void {
    // Load existing schedule if present
    const s = this.data.schedule;
    if (s) {
      this.enabled = s.enabled;
      this.frequency = s.frequency || 'weekly';
      this.dayOfWeek = s.dayOfWeek ?? 2;
      this.dayOfMonth = s.dayOfMonth ?? 1;
      this.minute = s.minute ?? 0;
      this.selectedUsers = [...(s.recipients || [])];

      // Convert 24h hour back to 12h + AM/PM
      if (s.hour !== undefined) {
        if (s.hour === 0) { this.hour = 12; this.ampm = 'AM'; }
        else if (s.hour < 12) { this.hour = s.hour; this.ampm = 'AM'; }
        else if (s.hour === 12) { this.hour = 12; this.ampm = 'PM'; }
        else { this.hour = s.hour - 12; this.ampm = 'PM'; }
      }
    }

    this.usersService.allUsers$.subscribe(users => {
      this.allUsers = users || [];
      this.cdr.markForCheck();
    });
  }

  /**
   * The zone the schedule actually fires in. The BACKEND owns this
   * (`ORG_TIMEZONES` in hc-all-orgs-scheduled-ops); the UI only labels it, so
   * this must stay in step with `serverless.yml` or the label misleads.
   * Absent ⇒ the backend's own default, so an unconfigured org still reads true.
   */
  private readonly orgTimeZone: string =
    (environment as any).orgTimeZone || 'America/New_York';

  /**
   * Short zone name for display ("PDT"/"PST"). Derived via Intl so it follows
   * daylight saving automatically instead of being hardcoded.
   */
  get timeZoneLabel(): string {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: this.orgTimeZone,
        timeZoneName: 'short',
      })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value || this.orgTimeZone;
    } catch {
      // Unknown/invalid zone — show the raw IANA name rather than nothing.
      return this.orgTimeZone;
    }
  }

  getScheduleSummary(): string {
    // Always name the zone. "every Monday at 8:00 AM" is ambiguous to anyone
    // not sitting in the org's timezone, and the send time is not negotiable
    // from the browser — the backend schedules it.
    const timeStr = `${this.hour}:${this.minute.toString().padStart(2, '0')} ${this.ampm} ${this.timeZoneLabel}`;

    switch (this.frequency) {
      case 'daily':
        return `This report will be emailed daily at ${timeStr}`;
      case 'weekly':
        const dayName = this.daysOfWeek.find(d => d.value === this.dayOfWeek)?.label || 'Tuesday';
        return `This report will be emailed every ${dayName} at ${timeStr}`;
      case 'monthly':
        return `This report will be emailed on day ${this.dayOfMonth} of each month at ${timeStr}`;
      default:
        return '';
    }
  }

  getAllRecipients(): string[] {
    const recipients = [...this.selectedUsers];
    if (this.externalEmails.trim()) {
      const external = this.externalEmails.split(',').map(e => e.trim()).filter(e => e.includes('@'));
      recipients.push(...external);
    }
    return [...new Set(recipients)];
  }

  save(): void {
    const hour24 = this.to24Hour(this.hour, this.ampm);
    const cronExpression = this.buildCronExpression(this.frequency, this.dayOfWeek, this.dayOfMonth, hour24, this.minute);

    const schedule: ReportSchedule = {
      enabled: this.enabled,
      cronExpression,
      action: 'email',
      recipients: this.getAllRecipients(),
      frequency: this.frequency,
      dayOfWeek: this.dayOfWeek,
      dayOfMonth: this.dayOfMonth,
      hour: hour24,
      minute: this.minute
    };

    this.dialogRef.close(schedule);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  private to24Hour(hour: number, ampm: 'AM' | 'PM'): number {
    if (ampm === 'AM') return hour === 12 ? 0 : hour;
    return hour === 12 ? 12 : hour + 12;
  }

  private buildCronExpression(
    frequency: string,
    dayOfWeek: number,
    dayOfMonth: number,
    hour: number,
    minute: number
  ): string {
    // AWS EventBridge cron format: cron(min hour day-of-month month day-of-week year)
    // Day of week: 1=Sunday in AWS cron (different from JS where 0=Sunday)
    const awsDow = dayOfWeek + 1; // JS 0-6 → AWS 1-7

    switch (frequency) {
      case 'daily':
        return `cron(${minute} ${hour} * * ? *)`;
      case 'weekly':
        return `cron(${minute} ${hour} ? * ${awsDow} *)`;
      case 'monthly':
        return `cron(${minute} ${hour} ${dayOfMonth} * ? *)`;
      default:
        return `cron(${minute} ${hour} * * ? *)`;
    }
  }
}
